import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@/context/ToastContext'
import { AccountService, SchoolLoginError } from '@common/services/account-service'

export const useLogin = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [emailError, setEmailError] = useState<string | null>(null)
  const [passwordError, setPasswordError] = useState<string | null>(null)
  const navigate = useNavigate()
  const { showErrorMessage } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setEmailError(null)
    setPasswordError(null)

    // バリデーション: 必須チェック
    const trimmedEmail = email.trim()
    const trimmedPassword = password.trim()
    let hasError = false

    if (!trimmedEmail) {
      setEmailError('メールアドレスは必須です')
      hasError = true
    }

    if (!trimmedPassword) {
      setPasswordError('パスワードは必須です')
      hasError = true
    }

    if (hasError) {
      return
    }

    setIsLoading(true)

    try {
      const accountService = new AccountService()
      const { isFirstLogin } = await accountService.loginAsSchool(
        trimmedEmail,
        trimmedPassword,
      )

      if (isFirstLogin) {
        navigate('/first-login')
      } else {
        navigate('/dashboards')
      }
    } catch (err: unknown) {
      if (err instanceof SchoolLoginError) {
        showErrorMessage(err.message)
        return
      }
      const authErr = err as { code?: string }
      console.error('Login error:', err)
      const errorMsg =
        authErr.code === 'auth/invalid-email'
          ? 'メールアドレスの形式が正しくありません'
          : authErr.code === 'auth/user-disabled'
            ? 'このアカウントは無効化されています'
            : ['auth/user-not-found', 'auth/wrong-password', 'auth/invalid-credential'].includes(
                  authErr.code ?? '',
                )
              ? 'メールアドレスまたはパスワードが正しくありません'
              : 'ログインに失敗しました。もう一度お試しください'

      if (authErr.code === 'auth/invalid-email') {
        setEmailError(errorMsg)
      } else if (
        ['auth/user-not-found', 'auth/wrong-password', 'auth/invalid-credential'].includes(
          authErr.code ?? '',
        )
      ) {
        setEmailError(errorMsg)
        setPasswordError(errorMsg)
      }
      showErrorMessage(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  const reset = () => {
    setEmail('')
    setPassword('')
    setShowPassword(false)
    setEmailError(null)
    setPasswordError(null)
  }

  return {
    email,
    setEmail,
    password,
    setPassword,
    showPassword,
    isLoading,
    emailError,
    passwordError,
    setEmailError,
    setPasswordError,
    handleSubmit,
    togglePasswordVisibility,
    reset,
  }
}

